<template>
    <v-form ref="form" class="pb-4">
        <v-row dense class="pb-4">
            <v-col cols="12" sm="6" md="2">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <div v-if="!itineraryDays.length && !isLoading" class="text-subtitle-1 px-2 pb-10">{{ $t('no_data') }}</div>
        <template v-if="isShowEditors">
            <v-row v-for="{description, number} in itineraryDays" :key="number" dense class="px-1">
                <v-col cols="12" md="2">
                    <span class="text-h6 text--secondary text-capitalize"> {{ $t('day') }} {{ number }} </span>
                </v-col>
                <v-col cols="12" md="10">
                    <NewHtmlEditor v-model="description[activeLangCode]" class="mb-2">
                        <div
                            v-if="activeLangCode"
                            class="fi fill-height px-3"
                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                        />
                    </NewHtmlEditor>
                </v-col>
            </v-row>
        </template>
        <v-row no-gutters class="gc-4">
            <v-btn class="save-btn" data-cy="save" :disabled="isLoading || !itineraryDays.length" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn class="cancel-btn" data-cy="cancel" :disabled="isLoading" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {nextTick} from 'vue'
    import {useFlagIconCode} from '~/composables/helpers'
    import {useFullContract} from '~/composables/form-helpers'
    const NewHtmlEditor = defineAsyncComponent(() => import('~/components/admin/NewHtmlEditor.vue'))

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const itineraryDays = ref([])
    const activeLangCode = ref<string>('en')
    const isShowEditors = ref(true)

    const app = useNuxtApp()
    const router = useRouter()
    const {t} = useI18n()

    const methods = {
        tour: 'ownTourContractsItineraryDay',
        customComplexTour: 'ownPackageContractsItineraryDay',
    }
    const appMethod = ref(null)

    onMounted(async () => {
        appMethod.value = methods[supplierStore.contract.serviceType]
        activeLangCode.value = app.$i18n.locale.value
        if (props.contractId && appMethod.value) {
            await loadData()
        }
    })

    watch(useFullContract, async () => {
        await loadData()
        flickEditors()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const res = await app.$api[appMethod.value].get(props.contractId, {locale: 'all'})
            itineraryDays.value = res.itineraryDays?.length
                ? res.itineraryDays
                : Array.from({length: supplierStore.fullContract.duration}, (_, i) => ({
                      description: {},
                      number: i + 1,
                  }))
        } catch (e) {
            itineraryDays.value = Array.from({length: supplierStore.fullContract.duration}, (_, i) => ({
                description: {},
                number: i + 1,
            }))
        } finally {
            isLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
        flickEditors()
    }

    const flickEditors = (): void => {
        isShowEditors.value = false
        nextTick().then(() => {
            isShowEditors.value = true
        })
    }

    const cancel = (): void => {
        router.push({name: 'supplierProducts', hash: '#customComplexTour'})
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            await app.$api[appMethod.value].put(props.contractId, {itineraryDays: itineraryDays.value})
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
